import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/Chips";
import { FreshnessIndicator } from "@/components/MotiveShared";
import { CheckCircle2 } from "lucide-react";

const fmtMins = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60), rem = m % 60;
  return h > 0 ? `${h}h ${rem}m` : `${rem}m`;
};

const Tile = ({ label, value, hint }) => (
  <div className="p-4 rounded-md border" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}>
    <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>{label}</div>
    <div className="mt-1 tabular font-display text-2xl" style={{ color: "var(--fg-primary)" }}>{value ?? "—"}</div>
    {hint && <div className="text-[11px] mt-1" style={{ color: "var(--fg-muted)" }}>{hint}</div>}
  </div>
);

const DUTY_COLOR = {
  driving: "var(--brand-lime)",
  on_duty_not_driving: "var(--brand-blue)",
  off_duty: "var(--fg-muted)",
  sleeper: "var(--status-hold)",
};

export default function DriverOperationsTab({ driverId }) {
  const [state, setState] = useState({ driver: null, log: [], watch: [], meta: null, err: null });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/motive/drivers/${driverId}`),
      api.get(`/motive/drivers/${driverId}/hos-log`),
      api.get(`/motive/ops-watch/driver/${driverId}`),
    ]).then(([d, l, w]) => {
      if (cancelled) return;
      setState({ driver: d.data.data, meta: d.data.meta, log: l.data.data || [], watch: w.data.data || [], err: null });
    }).catch((e) => {
      if (!cancelled) setState((s) => ({ ...s, err: e?.response?.data?.detail || "Motive not available for this driver" }));
    });
    return () => { cancelled = true; };
  }, [driverId]);

  if (state.err) return <div className="p-8 text-center" style={{ color: "var(--fg-secondary)" }} data-testid="driver-ops-empty">{state.err}</div>;
  if (!state.driver) return <div className="p-8 text-center" style={{ color: "var(--fg-muted)" }}>Loading Motive…</div>;

  const d = state.driver;
  const hos = d.hos_summary || {};
  const cp = d.current_driving_period;

  return (
    <div className="space-y-4" data-testid="driver-operations-tab">
      {/* Header */}
      <div className="rounded-md border p-4 flex flex-col md:flex-row md:items-center gap-3" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Driver Operations</div>
          <div className="text-[18px] font-semibold" style={{ color: "var(--fg-primary)" }} data-testid="driver-ops-name">{d.display_name}</div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <StatusPill status={d.current_duty_status} />
            {cp && cp.vehicle_label && (
              <span className="inline-flex items-center px-2 py-0.5 text-[11px] uppercase tracking-widest rounded-sm"
                style={{ border: "1px solid var(--border-default)", color: "var(--fg-secondary)" }}
                data-testid="current-vehicle-chip">
                on {cp.vehicle_label}
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 text-[10.5px] uppercase tracking-widest rounded-sm"
              style={{ background: "color-mix(in srgb, var(--brand-lime) 12%, transparent)",
                       border: "1px solid color-mix(in srgb, var(--brand-lime) 32%, transparent)",
                       color: "var(--fg-primary)" }}>
              ● CONNECTED — DEMO
            </span>
          </div>
        </div>
        <FreshnessIndicator lastUpdatedAt={state.meta?.last_updated_at} ageSeconds={state.meta?.age_seconds} freshness={state.meta?.freshness} />
      </div>

      {/* HOS tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hos-tiles">
        <Tile label="Duty Status" value={(d.current_duty_status || "").replace(/_/g, " ").toUpperCase()} />
        <Tile label="Drive Available" value={fmtMins(hos.drive_available_minutes)} />
        <Tile label="On-Duty Remaining" value={fmtMins(hos.on_duty_window_remaining_minutes)} />
        <Tile label="Cycle Remaining" value={fmtMins(hos.cycle_remaining_minutes)} />
      </div>

      {/* Current driving period */}
      {cp && (
        <div className="rounded-md border p-4" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }} data-testid="current-period-panel">
          <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Current Driving Period</div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-3 text-[13px]">
            <div><div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Start</div><div className="tabular">{new Date(cp.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div></div>
            <div><div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Duration</div><div className="tabular">{fmtMins(cp.duration_minutes)}</div></div>
            <div><div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Vehicle</div><div>{cp.vehicle_label}</div></div>
            <div><div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Miles</div><div className="tabular">{cp.miles}</div></div>
            <div><div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Status</div><div>{cp.tag || cp.status?.replace(/_/g, " ")}</div></div>
          </div>
        </div>
      )}

      {/* Ops Watch */}
      <div className="rounded-md border" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}>
        <div className="px-4 py-2 text-[11px] uppercase tracking-widest" style={{ color: "var(--fg-muted)", borderBottom: "1px solid var(--border-subtle)" }}>Operations Watch</div>
        <ul>
          {state.watch.map((w, i) => (
            <li key={i} className="px-4 py-3 flex items-center gap-3 text-[13px]"
              style={{ borderTop: i > 0 ? "1px solid var(--border-subtle)" : "none" }}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999,
                background: w.severity === "normal" ? "var(--brand-lime)"
                  : w.severity === "watch" ? "var(--status-hold)"
                    : "var(--status-maintenance)" }} />
              <strong style={{ color: "var(--fg-primary)" }}>{w.label}</strong>
              {w.detail && <span style={{ color: "var(--fg-secondary)" }}>· {w.detail}</span>}
            </li>
          ))}
        </ul>
      </div>

      {/* Today's Log */}
      <div className="rounded-md border" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }} data-testid="hos-log-timeline">
        <div className="px-4 py-2 text-[11px] uppercase tracking-widest" style={{ color: "var(--fg-muted)", borderBottom: "1px solid var(--border-subtle)" }}>Today's Log</div>
        <ol className="p-4 space-y-2">
          {state.log.map((ev, i) => (
            <li key={i} className="flex items-center gap-3 text-[13px]">
              <span className="tabular" style={{ color: "var(--fg-secondary)", width: 60 }}>{new Date(ev.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: DUTY_COLOR[ev.duty_status] || "var(--fg-muted)" }} />
              <strong style={{ color: "var(--fg-primary)" }}>{(ev.duty_status || "").replace(/_/g, " ").toUpperCase()}</strong>
              <span style={{ color: "var(--fg-secondary)" }}>· {fmtMins(ev.duration_minutes)}{ev.vehicle && ` · ${ev.vehicle}`}{ev.note && ` · ${ev.note}`}</span>
            </li>
          ))}
          {state.log.length === 0 && <li className="text-[13px]" style={{ color: "var(--fg-muted)" }}>No events logged today.</li>}
        </ol>
      </div>

      <div className="text-[11.5px]" style={{ color: "var(--fg-muted)" }}>
        Read-only. Update in Motive.
      </div>
    </div>
  );
}
